import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4500'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased to 30 seconds for slower database queries
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`)
    return config
  },
  (error) => {
    console.error('API Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`)
    return response
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message)
    return Promise.reject(error)
  }
)

// Game-related API calls
export const fetchGames = async () => {
  try {
    const response = await api.get('/api/games/')
    return response.data.games || []
  } catch (error) {
    console.error('Error fetching games:', error)
    throw error
  }
}

export const fetchLiveGames = async () => {
  try {
    const response = await api.get('/api/games/live')
    return response.data.live_games || []
  } catch (error) {
    console.error('Error fetching live games:', error)
    throw error
  }
}

export const fetchGameById = async (gameId: string) => {
  try {
    const response = await api.get(`/api/games/${gameId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching game:', error)
    throw error
  }
}

export const fetchCompleteGameDetails = async (gameId: string) => {
  try {
    const response = await api.get(`/api/games/${gameId}/complete`)
    return response.data
  } catch (error) {
    console.error('Error fetching complete game details:', error)
    throw error
  }
}

export const fetchPrematchScrapingHistory = async (gameId: string) => {
  try {
    const response = await api.get(`/api/games/${gameId}/prematch-scraping-history`)
    return response.data
  } catch (error) {
    console.error('Error fetching prematch scraping history:', error)
    throw error
  }
}

// Historical data API calls
export const fetchHistoricalTrend = async (gameId: string) => {
  try {
    const response = await api.get(`/api/data/historical/trend/${gameId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching historical trend:', error)
    throw error
  }
}

export const fetchGamePrediction = async (gameId: string) => {
  try {
    // Backend does not expose /api/matches/{id}/prediction; map from complete game details
    const response = await api.get(`/api/games/${gameId}/complete`)
    const data = response.data
    const g = data?.game || {}
    const rc = data?.reddit_comments || { total_count: 0, summary: { home_fans: 0, away_fans: 0, neutral_fans: 0 } }
    const homeConf = Number(g.home_confidence_score ?? 0)
    const awayConf = Number(g.away_confidence_score ?? 0)
    const homeAvg = Number(g.home_confidence_avg ?? homeConf)
    const awayAvg = Number(g.away_confidence_avg ?? awayConf)
    const compHome = Number(g.composite_home_score ?? homeConf)
    const compAway = Number(g.composite_away_score ?? awayConf)

    return {
      game_id: g.game_id,
      home_team: g.home_team || g.home_team_hashtag,
      away_team: g.away_team || g.away_team_hashtag,
      home_confidence: homeConf,
      away_confidence: awayConf,
      predicted_winner: g.predicted_winner || 'DRAW',
      prediction_strength: g.prediction_strength || 'UNKNOWN',
      data_sources: {
        reddit_comments: rc.total_count || 0,
        twitter_tweets: 0,
        total_sentiment_data: rc.total_count || 0
      },
      fan_breakdown: {
        home_fans: rc.summary?.home_fans || 0,
        away_fans: rc.summary?.away_fans || 0,
        neutral_fans: rc.summary?.neutral_fans || 0
      },
      reliability_metrics: {
        overall_confidence: 0.0,
        overall_reliability: g.prediction_reliability || 'N/A',
        home_team_reliability: {
          confidence_score: 0.0,
          reliability_tier: g.prediction_reliability || 'LOW',
          sample_size: rc.summary?.home_fans || 0
        },
        away_team_reliability: {
          confidence_score: 0.0,
          reliability_tier: g.prediction_reliability || 'LOW',
          sample_size: rc.summary?.away_fans || 0
        },
        data_quality_breakdown: {
          reddit_count: rc.total_count || 0,
          twitter_count: 0,
          total_count: rc.total_count || 0
        }
      },
      metrics: {
        average: {
          home: homeAvg,
          away: awayAvg,
          difference: Math.abs(homeAvg - awayAvg)
        },
        // Cumulative not directly available; fall back to confidence
        cumulative: {
          home: homeConf,
          away: awayConf,
          difference: Math.abs(homeConf - awayConf)
        },
        composite: {
          home: compHome,
          away: compAway,
          difference: Math.abs(compHome - compAway)
        }
      }
    }
  } catch (error) {
    console.error('Error fetching game prediction:', error)
    throw error
  }
}

// Reddit comments API
export const fetchRedditComments = async (gameId: string, limit = 500) => {
  try {
    const response = await api.get(`/api/reddit/comments/${gameId}`, {
      params: { limit }
    })
    return response.data
  } catch (error) {
    console.error('Error fetching reddit comments:', error)
    throw error
  }
}

// Sentiment analysis API calls
export const analyzeTweets = async (texts: string[]) => {
  try {
    const response = await api.post('/api/analyze', { texts })
    return response.data
  } catch (error) {
    console.error('Error analyzing tweets:', error)
    throw error
  }
}

// Game state management
export const updateGameState = async (gameId: string, status: string) => {
  try {
    const response = await api.post('/api/games/state_update', {
      game_id: gameId,
      status: status
    })
    return response.data
  } catch (error) {
    console.error('Error updating game state:', error)
    throw error
  }
}

// Health check
export const healthCheck = async () => {
  try {
    const response = await api.get('/health')
    return response.data
  } catch (error) {
    console.error('Health check failed:', error)
    throw error
  }
}

// Prediction parameters API
export const fetchPredictionParameters = async () => {
  try {
    const response = await api.get('/api/games/prediction-parameters')
    return response.data
  } catch (error) {
    console.error('Error fetching prediction parameters:', error)
    throw error
  }
}

// Analytics API
export const fetchAnalytics = async () => {
  try {
    const response = await api.get('/api/analytics/')
    return response.data
  } catch (error) {
    console.error('Error fetching analytics:', error)
    throw error
  }
}

export default api
